import { useState } from "react";
import { useOverview, useAdvancedStats } from "@/hooks/useData";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  GeneralInfo, EquityPanel, StrategyTable, SymbolBars,
  SessionsPanel, HourlyChart, YearlyPanel, PerformanceDetail,
} from "./OverviewWidgets";
import MonthlyReturnsPanel from "./MonthlyReturnsModal";

const RANGES = [
  { v: "today", l: "Today" }, { v: "week", l: "Week" }, { v: "month", l: "Month" },
  { v: "year", l: "Year" }, { v: "all", l: "All" },
];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

export default function OverviewDashboard({
  accountId, currency = "USD",
  range: rangeProp, onRangeChange,
  symbol: symbolProp, onSymbolChange, symbols = [],
  shareToken,
}) {
  const [rangeState, setRangeState] = useState("month");
  const range = rangeProp ?? rangeState;
  const setRange = onRangeChange ?? setRangeState;
  const [symbolState, setSymbolState] = useState("ALL");
  const symbol = symbolProp ?? symbolState;
  const setSymbol = onSymbolChange ?? setSymbolState;
  const [year, setYear] = useState(null); // null = current year
  const [month, setMonth] = useState(null); // set when a specific month is picked from Monthly returns

  const { data, isLoading } = useOverview(accountId, range, symbol, year || undefined, month || undefined, shareToken);
  const { data: stats } = useAdvancedStats(accountId, range, symbol, shareToken);

  const selectedYear = year || data?.selected_year || new Date().getFullYear();
  const shiftYear = (delta) => setYear(selectedYear + delta);

  const selectMonth = (y, m) => { setYear(y); setMonth(m); setRange("month"); };
  const clearMonth = () => setMonth(null);
  const selectedMonthLabel = range === "month" && month ? `${MONTH_NAMES[month - 1]} ${year || new Date().getFullYear()}` : null;

  if (isLoading || !data) {
    return (
      <div className="grid gap-4 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="overview-dashboard-view">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-full bg-surface-2 p-1 w-fit">
          {RANGES.map((r) => (
            <button key={r.v} type="button" onClick={() => { setRange(r.v); setMonth(null); }}
              className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors",
                range === r.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
              data-testid={`overview-range-${r.v}`}>
              {r.l}
            </button>
          ))}
        </div>
        {symbols.length > 0 && (
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="overview-symbol-filter">
              <SelectValue placeholder="All symbols" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All symbols</SelectItem>
              {symbols.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <GeneralInfo stats={stats} currency={currency} />
        <EquityPanel curve={data.equity_curve} currency={currency} selectedLabel={selectedMonthLabel} onClear={clearMonth} />
      </div>

      <MonthlyReturnsPanel accountId={accountId} currency={currency} symbol={symbol} onSelectMonth={selectMonth} shareToken={shareToken} />

      <div className="grid gap-4 lg:grid-cols-3">
        <StrategyTable rows={data.strategy_table} currency={currency} />
        <SymbolBars symbols={data.symbols} currency={currency} />
        <SessionsPanel sessions={data.sessions} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <HourlyChart hours={data.hours} />
        <YearlyPanel months={data.months} years={data.years} selectedYear={selectedYear}
          onPrevYear={() => shiftYear(-1)} onNextYear={() => shiftYear(1)} currency={currency} />
        <PerformanceDetail detail={data.performance_detail} currency={currency} />
      </div>
    </div>
  );
}
